import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, fetchAllPaginated, fetchSchoolsSimple } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import { includesAllWords } from '../utils/normalizeText';
import { Link } from 'react-router-dom';
import { useSelectedCity } from '../contexts/SelectedCityContext';

// Sugestão de login a partir do nome: "Maria das Dores Silva" -> "maria.silva".
// É só uma sugestão — o campo continua editável, e é assim que a mesma pessoa
// pode ter dois acessos ("maria.silva" e "maria.silva.secretaria").
function sugerirUsername(firstName, lastName) {
  const partes = `${firstName || ''} ${lastName || ''}`
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '';
  return partes.length > 1 ? `${partes[0]}.${partes[partes.length - 1]}` : partes[0];
}

// Sem sufixo o primeiro acesso; do segundo em diante o papel entra no nome,
// que é como a pessoa distingue um do outro na tela de escolha do login.
function usernameDoPapel(base, papel, indice) {
  if (!base) return '';
  return indice === 0 ? base : `${base}.${papel}`;
}

// Perfis presos a escolas: a(s) escola(s) atribuída(s) definem tudo o que
// eles enxergam. Sem escola atribuída, a conta não vê nada.
const temEscolaAtribuida = (role) => role === 'supervisor' || role === 'diretor';

function Usuarios() {
  const { user, getUserCity, hasRole } = useAuth();
  const { darkMode } = useTheme();
  const userCity = getUserCity();

  // Estados principais
  const [usuarios, setUsuarios] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Autocadastro por link: quem cria acessos aqui (Administrador e
  // Coordenador) também gera os links e aprova quem se cadastrou por eles —
  // na página Usuários › Autocadastro. Aqui fica só o atalho com os
  // pendentes do município escolhido no menu. Secretário só visualiza.
  const geraAutocadastro = user?.role === ROLES.ADMIN || user?.role === ROLES.COORDENADOR;
  const { selectedCityId } = useSelectedCity();
  const [pendentesAutocadastro, setPendentesAutocadastro] = useState(0);

  // Filtros
  const [filtroRole, setFiltroRole] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal de criação/edição
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    // Um acesso por perfil marcado. `usernames` guarda o login de cada um,
    // editável antes de salvar.
    roles: [],
    usernames: {},
    cpf: '',
    first_name: '',
    last_name: '',
    email: '',
    role: 'gestor',
    city: '',
    phone: '',
    password: '',
    password_confirm: '',
    is_active: true,
    id_teacher: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Enquanto o coordenador não mexer no campo, o usuário acompanha o nome.
  // Depois que ele edita, paramos de sobrescrever o que ele escreveu.
  const [usernameEditado, setUsernameEditado] = useState(false);
  // Papéis cujo login o coordenador digitou à mão: paramos de sobrescrevê-los.
  const [usernamesEditados, setUsernamesEditados] = useState({});

  // Os outros acessos da mesma pessoa, e os perfis que ela ainda não tem.
  // Um acesso tem um perfil só; acumular funções é ganhar outro acesso, e é
  // daqui que ele nasce para quem já está cadastrado.
  const [acessosDaPessoa, setAcessosDaPessoa] = useState([]);
  const [perfisDisponiveis, setPerfisDisponiveis] = useState([]);
  const [carregandoAcessos, setCarregandoAcessos] = useState(false);
  const [novosPerfis, setNovosPerfis] = useState([]);
  const [novosLogins, setNovosLogins] = useState({});
  const [novosLoginsEditados, setNovosLoginsEditados] = useState({});

  // Um acesso que a pessoa não exerce mais precisa sair de circulação. Não é
  // trocar o perfil deste acesso: quem deixou de ser gestor e virou
  // coordenador tem duas contas, e é a de gestor que tem de parar.
  const [mexendoNoAcesso, setMexendoNoAcesso] = useState(null);

  const recarregarAcessos = async (idBase) => {
    try {
      const acessos = await apiGet(`/users/${idBase}/acessos/`);
      setAcessosDaPessoa(acessos.accounts || []);
      setPerfisDisponiveis(acessos.roles_disponiveis || []);
    } catch (e) {
      console.error('Erro ao recarregar os acessos:', e);
    }
  };

  const alternarAtivoDoAcesso = async (conta) => {
    setMexendoNoAcesso(conta.id);
    try {
      await apiPatch(`/users/${conta.id}/`, { is_active: !conta.is_active });
      await recarregarAcessos(editingUser.id);
      await fetchData();
    } catch (e) {
      setFormErrors((f) => ({
        ...f,
        acessos: e?.response?.error || 'Não foi possível mudar a situação deste acesso.',
      }));
    } finally {
      setMexendoNoAcesso(null);
    }
  };

  // Excluir apaga o acesso e o que ele lançou fica sem dono. Desativar é o
  // caminho normal; excluir só quando a conta nasceu errada.
  const excluirAcesso = async (conta) => {
    if (!window.confirm(
      `Excluir o acesso @${conta.username} (${conta.role_display || conta.role})?\n\n` +
      'Isso apaga a conta. Se ela já lançou frequência ou notas, prefira desativar.'
    )) return;
    setMexendoNoAcesso(conta.id);
    try {
      await apiDelete(`/users/${conta.id}/`);
      await recarregarAcessos(editingUser.id);
      await fetchData();
    } catch (e) {
      setFormErrors((f) => ({
        ...f,
        acessos: e?.response?.error || 'Não foi possível excluir este acesso.',
      }));
    } finally {
      setMexendoNoAcesso(null);
    }
  };

  const limparAcrescimo = () => {
    setAcessosDaPessoa([]);
    setPerfisDisponiveis([]);
    setNovosPerfis([]);
    setNovosLogins({});
    setNovosLoginsEditados({});
  };

  // Aqui o nome simples já pertence à conta que existe, então todo acesso
  // novo leva o perfil no fim — nunca um "maria.silva2", que não diz de qual
  // acesso é.
  const alternarNovoPerfil = (papel, firstName, lastName) => {
    setNovosPerfis((atuais) => {
      const proximos = atuais.includes(papel)
        ? atuais.filter((p) => p !== papel)
        : [...atuais, papel];
      const base = sugerirUsername(firstName, lastName);
      setNovosLogins((logins) => {
        const saida = {};
        proximos.forEach((p) => {
          saida[p] = novosLoginsEditados[p] ? (logins[p] || '') : (base ? `${base}.${p}` : '');
        });
        return saida;
      });
      return proximos;
    });
  };

  // Recalcula os logins dos perfis marcados a partir do nome, preservando os
  // que já foram editados à mão.
  const recalcularUsernames = (firstName, lastName, papeis, atuais, editados) => {
    const base = sugerirUsername(firstName, lastName);
    const saida = {};
    papeis.forEach((papel, indice) => {
      saida[papel] = editados[papel]
        ? (atuais[papel] || '')
        : usernameDoPapel(base, papel, indice);
    });
    return saida;
  };

  const alternarPapel = (papel) => {
    setFormData((f) => {
      const papeis = f.roles.includes(papel)
        ? f.roles.filter((r) => r !== papel)
        : [...f.roles, papel];
      return {
        ...f,
        roles: papeis,
        usernames: recalcularUsernames(
          f.first_name, f.last_name, papeis, f.usernames, usernamesEditados
        ),
      };
    });
  };

  // Modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSchools, setUserSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  // Modal de atribuição de escolas (Supervisor e Diretor)
  const [showSchoolsModal, setShowSchoolsModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [savingSchools, setSavingSchools] = useState(false);
  const [buscaEscola, setBuscaEscola] = useState('');

  // Modal de reset de senha
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');
  // O caminho normal é mandar o link por e-mail: quem escolhe a senha é o
  // dono dela. Definir na mão continua existindo para quem não tem e-mail
  // cadastrado — sem endereço, não há link a enviar.
  const [definirSenhaNaMao, setDefinirSenhaNaMao] = useState(false);
  const [passwordResetOk, setPasswordResetOk] = useState('');

  // Sub-form inline para cadastrar um novo Professor (TbTeacher)
  // diretamente do modal de criação/edição de usuário Professor.
  const [showCreateTeacher, setShowCreateTeacher] = useState(false);
  const [newTeacherForm, setNewTeacherForm] = useState({ teacher_name: '', teacher_serial: '' });
  const [creatingTeacher, setCreatingTeacher] = useState(false);
  const [createTeacherError, setCreateTeacherError] = useState('');

  // Buscar dados
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [usuariosData, cidadesData, rolesData, escolasData, professoresData] = await Promise.all([
        fetchAllPaginated('/users/'),
        fetchAllPaginated('/cities/'),
        apiGet('/users/roles/'),
        fetchSchoolsSimple(),
        fetchAllPaginated('/teachers/'),
      ]);

      setUsuarios(usuariosData);
      setCidades(cidadesData);
      setRoles(rolesData);
      setEscolas(escolasData);
      setProfessores(professoresData || []);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Pendentes do autocadastro no município escolhido no menu (o atalho
  // mostra o número; a revisão é na página Usuários › Autocadastro).
  useEffect(() => {
    if (!geraAutocadastro) return;
    apiGet('/autocadastro/pedidos/contagem/',
      user?.role === ROLES.ADMIN && selectedCityId ? { city: selectedCityId } : {})
      .then((r) => setPendentesAutocadastro(r?.pendentes || 0))
      .catch(() => {});
  }, [geraAutocadastro, user?.role, selectedCityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar usuários
  const usuariosFiltrados = usuarios.filter(u => {
    const matchRole = !filtroRole || u.role === filtroRole;
    const matchCidade = !filtroCidade || String(u.city) === filtroCidade;
    const matchStatus = filtroStatus === '' ||
      (filtroStatus === 'ativo' && u.is_active) ||
      (filtroStatus === 'inativo' && !u.is_active);
    // Nome completo junto: "rita de cascia" atravessa first_name e last_name.
    const matchSearch = !searchTerm.trim() ||
      includesAllWords(`${u.first_name || ''} ${u.last_name || ''} ${u.username || ''} ${u.email || ''}`, searchTerm) ||
      u.cpf?.replace(/\D/g, '').includes(searchTerm.replace(/\D/g, '') || '\u0000');

    return matchRole && matchCidade && matchStatus && matchSearch;
  });

  // Lista do modal "Gerenciar Escolas", filtrada pela busca. A seleção
  // continua valendo para escolas que a busca esconde.
  const escolasDoModal = escolas.filter(escola =>
    includesAllWords(`${escola.school || ''} ${escola.city_name || ''}`, buscaEscola)
  );

  // Verificar permissões - apenas Admin e Coordenador podem criar
  const canCreate = hasRole(ROLES.ADMIN) || hasRole(ROLES.COORDENADOR) || hasRole(ROLES.REFERENCIA_TECNICA);
  const canEdit = hasRole(ROLES.ADMIN) || hasRole(ROLES.COORDENADOR) || hasRole(ROLES.GERENTE) || hasRole(ROLES.REFERENCIA_TECNICA);
  const canDelete = hasRole(ROLES.ADMIN);
  // Mesma regra do backend (`assign_schools`): só Administrador e Coordenador
  // gravam a atribuição. Mostrar o botão a mais gente só levava a um erro.
  const canAssignSchools = hasRole(ROLES.ADMIN) || hasRole(ROLES.COORDENADOR);

  // Reset do sub-form de criação de Professor inline
  const resetCreateTeacherForm = () => {
    setShowCreateTeacher(false);
    setNewTeacherForm({ teacher_name: '', teacher_serial: '' });
    setCreateTeacherError('');
  };

  // Abrir modal para criar novo usuário
  const handleCreate = () => {
    setEditingUser(null);
    setUsernameEditado(false);
    setUsernamesEditados({});
    limparAcrescimo();
    setFormData({
      username: '',
      roles: [],
      usernames: {},
      cpf: '',
      first_name: '',
      last_name: '',
      email: '',
      role: 'gestor',
      city: userCity?.id || '',
      phone: '',
      password: '',
      password_confirm: '',
      is_active: true,
      id_teacher: '',
    });
    setFormErrors({});
    resetCreateTeacherForm();
    setShowModal(true);
  };

  // Abrir modal para editar usuário
  const handleEdit = async (usuario) => {
    setEditingUser(usuario);
    limparAcrescimo();
    setCarregandoAcessos(true);
    try {
      const acessos = await apiGet(`/users/${usuario.id}/acessos/`);
      setAcessosDaPessoa(acessos.accounts || []);
      setPerfisDisponiveis(acessos.roles_disponiveis || []);
    } catch (e) {
      // Sem a lista, a edição do acesso continua funcionando: só não dá para
      // acrescentar outro perfil daqui.
      console.error('Erro ao carregar os acessos da pessoa:', e);
    } finally {
      setCarregandoAcessos(false);
    }
    setUsernameEditado(true);
    setFormData({
      // Editar mexe num acesso só: o perfil dele continua sendo escolha única.
      roles: [],
      usernames: {},
      username: usuario.username || '',
      cpf: usuario.cpf || '',
      first_name: usuario.first_name || '',
      last_name: usuario.last_name || '',
      email: usuario.email || '',
      role: usuario.role || 'gestor',
      city: usuario.city || '',
      phone: usuario.phone || '',
      password: '',
      password_confirm: '',
      is_active: usuario.is_active,
      id_teacher: usuario.id_teacher || '',
    });
    setFormErrors({});
    resetCreateTeacherForm();
    setShowModal(true);
  };

  // Cria um novo TbTeacher inline e já vincula ao formulário do usuário.
  // Pré-preenche o nome a partir de first_name/last_name quando vazio.
  const handleCreateTeacher = async () => {
    setCreateTeacherError('');
    const name = newTeacherForm.teacher_name.trim();
    const serial = String(newTeacherForm.teacher_serial).trim();
    if (!name) {
      setCreateTeacherError('Nome do professor é obrigatório.');
      return;
    }
    if (!serial) {
      setCreateTeacherError('Matrícula é obrigatória.');
      return;
    }
    setCreatingTeacher(true);
    try {
      const created = await apiPost('/teachers/', {
        teacher_name: name,
        teacher_serial: parseInt(serial, 10),
        status: 'active',
        subjects: [],
        schools: [],
      });
      setProfessores(prev => [...prev, created]);
      setFormData(prev => ({ ...prev, id_teacher: created.id }));
      resetCreateTeacherForm();
    } catch (err) {
      console.error('Erro ao criar professor inline:', err);
      const msg =
        err.response?.data?.teacher_serial?.[0] ||
        err.response?.data?.teacher_name?.[0] ||
        err.response?.data?.detail ||
        'Erro ao cadastrar professor. Verifique os dados.';
      setCreateTeacherError(msg);
    } finally {
      setCreatingTeacher(false);
    }
  };

  // Buscar escolas do usuário (Supervisor e Diretor)
  const fetchUserSchools = async (userId) => {
    setLoadingSchools(true);
    try {
      const response = await apiGet(`/users/${userId}/schools/`);
      setUserSchools(response.schools || []);
    } catch (err) {
      console.error('Erro ao buscar escolas do usuário:', err);
      setUserSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  };

  // Abrir modal de detalhes
  const handleViewDetails = async (usuario) => {
    setSelectedUser(usuario);
    setShowDetailsModal(true);

    if (temEscolaAtribuida(usuario.role)) {
      await fetchUserSchools(usuario.id);
    } else {
      setUserSchools([]);
    }
  };

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'Nome é obrigatório';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Sobrenome é obrigatório';
    }

    // O CPF é opcional: quem identifica o acesso é o nome de usuário. Isso é o
    // que permite dois acessos separados para a mesma pessoa.
    const cpfDigits = (formData.cpf || '').replace(/\D/g, '');
    if (cpfDigits && cpfDigits.length !== 11) {
      errors.cpf = 'CPF deve ter 11 dígitos';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (editingUser) {
      if (!formData.role) errors.role = 'Perfil é obrigatório';
      if (novosPerfis.length > 0) {
        const logins = novosPerfis.map((p) => (novosLogins[p] || '').trim());
        if (logins.some((l) => !l)) {
          errors.novosAcessos = 'Cada acesso novo precisa de um nome de usuário.';
        } else if (new Set(logins.map((l) => l.toLowerCase())).size !== logins.length) {
          errors.novosAcessos = 'Os acessos novos não podem repetir o mesmo login.';
        } else if (logins.some((l) => l.toLowerCase() === (formData.username || '').toLowerCase())) {
          errors.novosAcessos = 'O login novo não pode ser igual ao deste acesso.';
        }
      }
    } else if (formData.roles.length === 0) {
      errors.role = 'Marque ao menos um perfil';
    } else {
      const logins = formData.roles.map((p) => (formData.usernames[p] || '').trim());
      if (logins.some((l) => !l)) {
        errors.usernames = 'Cada acesso precisa de um nome de usuário';
      } else if (logins.some((l) => !/^[A-Za-z0-9._@+-]+$/.test(l))) {
        errors.usernames = 'Use apenas letras, números e os sinais . _ - @ +';
      } else if (new Set(logins.map((l) => l.toLowerCase())).size !== logins.length) {
        errors.usernames = 'Os acessos não podem ter o mesmo nome de usuário';
      }
    }

    // Para perfil Professor, exigir vínculo com cadastro de Professor (TbTeacher)
    const temProfessor = editingUser
      ? formData.role === 'professor'
      : formData.roles.includes('professor');
    if (temProfessor && !formData.id_teacher) {
      errors.id_teacher = 'Selecione um cadastro de Professor ou crie um novo.';
    }

    // Senha obrigatória apenas na criação
    if (!editingUser) {
      if (!formData.password) {
        errors.password = 'Senha é obrigatória';
      } else if (formData.password.length < 6) {
        errors.password = 'Senha deve ter no mínimo 6 caracteres';
      }

      if (formData.password !== formData.password_confirm) {
        errors.password_confirm = 'As senhas não coincidem';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Salvar usuário (criar ou atualizar)
  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingUser) {
        // Atualizar usuário
        const dataToSend = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          city: formData.city || null,
          phone: formData.phone,
          cpf: formData.cpf || null,
          is_active: formData.is_active,
          // O vínculo com o RH vale para qualquer perfil, não só professor:
          // mandar null aqui desfazia o vínculo de diretores e supervisores,
          // que voltavam a aparecer como "sem acesso" na tela de Atribuição.
          id_teacher: formData.id_teacher || null,
        };
        await apiPut(`/users/${editingUser.id}/`, dataToSend);

        // Os acessos novos vêm depois da edição de propósito: o CPF e o nome
        // que eles herdam são os que acabaram de ser salvos.
        if (novosPerfis.length > 0) {
          await apiPost(`/users/${editingUser.id}/acessos/`, {
            roles: novosPerfis,
            usernames: Object.fromEntries(
              novosPerfis.map((p) => [p, (novosLogins[p] || '').trim()])
            ),
          });
        }
      } else {
        // Criar — um acesso por perfil marcado, numa transação só
        const dataToSend = {
          roles: formData.roles,
          usernames: Object.fromEntries(
            formData.roles.map((p) => [p, (formData.usernames[p] || '').trim()])
          ),
          cpf: formData.cpf || null,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          city: formData.city || null,
          phone: formData.phone,
          password: formData.password,
          password_confirm: formData.password_confirm,
          id_teacher: formData.roles.includes('professor') ? (formData.id_teacher || null) : null,
        };
        await apiPost('/users/', dataToSend);
      }

      setShowModal(false);
      resetCreateTeacherForm();
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar usuário:', err);
      // O serializer manda lista (`{"cpf": ["..."]}`), mas a rota de acessos
      // manda a frase direto (`{"cpf": "Preencha o CPF..."}`). Com `?.[0]`
      // numa string, a tela mostrava só a primeira letra: "P".
      const primeira = (valor) => (Array.isArray(valor) ? valor[0] : valor);
      const campoUsername = primeira(err.response?.data?.username);
      if (campoUsername) {
        setFormErrors({ username: campoUsername });
        return;
      }
      // Erro por perfil, quando vários acessos foram enviados juntos
      const porConta = err.response?.data?.accounts;
      if (porConta) {
        const detalhes = Object.entries(porConta)
          .map(([papel, campos]) => {
            const rotulo = roles.find((r) => r.value === papel)?.label || papel;
            const msg = Object.values(campos).flat().join(' ');
            return `${rotulo}: ${msg}`;
          })
          .join(' · ');
        setFormErrors({ usernames: detalhes });
        return;
      }
      const erroUsernames = err.response?.data?.usernames;
      if (erroUsernames) {
        setFormErrors({ usernames: String(erroUsernames) });
        return;
      }
      const errorMessage = primeira(err.response?.data?.cpf) ||
        primeira(err.response?.data?.email) ||
        primeira(err.response?.data?.password) ||
        primeira(err.response?.data?.roles) ||
        err.response?.data?.detail ||
        err.message ||
        'Erro ao salvar usuário';
      setFormErrors({ submit: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (usuario) => {
    setUserToDelete(usuario);
    setShowDeleteModal(true);
  };

  // Executar exclusão
  const handleDelete = async (usuario) => {
    await apiDelete(`/users/${usuario.id}/`);
    await fetchData();
  };

  // Alternar status ativo/inativo
  const handleToggleActive = async (usuario) => {
    try {
      await apiPut(`/users/${usuario.id}/`, {
        ...usuario,
        is_active: !usuario.is_active,
        city: usuario.city || null,
      });
      fetchData();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      alert('Erro ao alterar status do usuário');
    }
  };

  // Abrir modal de atribuição de escolas
  const handleManageSchools = async (usuario) => {
    setSelectedManager(usuario);
    setBuscaEscola('');
    setLoadingSchools(true);
    setShowSchoolsModal(true);

    try {
      const response = await apiGet(`/users/${usuario.id}/schools/`);
      setSelectedSchools((response.schools || []).map(s => s.id));
    } catch (err) {
      // Salvar grava a lista exata: abrir com a lista vazia e salvar
      // tiraria do login as escolas que ele já tem.
      console.error('Erro ao buscar escolas:', err);
      alert('Não foi possível carregar as escolas deste usuário. Tente novamente.');
      setShowSchoolsModal(false);
      setSelectedManager(null);
      setSelectedSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  };

  // Salvar atribuição de escolas
  const handleSaveSchools = async () => {
    if (!selectedManager) return;

    setSavingSchools(true);
    try {
      await apiPost(`/users/${selectedManager.id}/assign_schools/`, {
        schools: selectedSchools
      });
      setShowSchoolsModal(false);
      fetchData();
    } catch (err) {
      console.error('Erro ao atribuir escolas:', err);
      alert('Erro ao atribuir escolas.');
    } finally {
      setSavingSchools(false);
    }
  };

  // Toggle escola selecionada
  const toggleSchoolSelection = (schoolId) => {
    setSelectedSchools(prev =>
      prev.includes(schoolId)
        ? prev.filter(id => id !== schoolId)
        : [...prev, schoolId]
    );
  };

  // Abrir modal de reset de senha
  const handleResetPasswordClick = (usuario) => {
    setUserToResetPassword(usuario);
    setNewPassword('');
    setNewPasswordConfirm('');
    setPasswordResetError('');
    setPasswordResetOk('');
    // Sem e-mail cadastrado não há link a enviar: já abre no formulário.
    setDefinirSenhaNaMao(!usuario.email);
    setShowResetPasswordModal(true);
  };

  // Manda o link de redefinição para o e-mail cadastrado. A senha atual
  // continua valendo até a pessoa usar o link.
  const handleEnviarLinkSenha = async () => {
    if (!userToResetPassword) return;
    setResettingPassword(true);
    setPasswordResetError('');
    setPasswordResetOk('');
    try {
      const resp = await apiPost(
        `/users/${userToResetPassword.id}/enviar-redefinicao-senha/`, {},
      );
      setPasswordResetOk(resp.message || 'Link enviado.');
    } catch (err) {
      console.error('Erro ao enviar o link de redefinição:', err);
      const dados = err.response?.data || {};
      setPasswordResetError(
        [dados.error, dados.error_detail].filter(Boolean).join(' ')
        || err.message
        || 'Não foi possível enviar o e-mail.'
      );
    } finally {
      setResettingPassword(false);
    }
  };

  // Executar reset de senha (admin define nova senha)
  const handleResetPassword = async () => {
    if (!userToResetPassword) return;

    if (!newPassword || newPassword.length < 6) {
      setPasswordResetError('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setPasswordResetError('As senhas não coincidem');
      return;
    }

    setResettingPassword(true);
    setPasswordResetError('');

    try {
      // Use admin endpoint to reset password
      await apiPost(`/users/${userToResetPassword.id}/admin_reset_password/`, {
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      setShowResetPasswordModal(false);
      alert('Senha alterada com sucesso!');
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      setPasswordResetError(err.response?.data?.error || err.message || 'Erro ao redefinir senha');
    } finally {
      setResettingPassword(false);
    }
  };

  // Limpar filtros
  const clearFilters = () => {
    setFiltroRole('');
    setFiltroCidade('');
    setFiltroStatus('');
    setSearchTerm('');
  };

  // Obter nome do role
  const getRoleLabel = (roleValue) => {
    const role = roles.find(r => r.value === roleValue);
    return role?.label || roleValue;
  };

  // Obter cor do badge do role
  const getRoleBadgeColor = (role) => {
    const colors = {
      administrador: 'bg-[#2E4974]/20 text-[#2E4974]',
      coordenador: 'bg-[#0974BA]/20 text-[#0974BA]',
      diretor: 'bg-[#F9B047]/20 text-[#B07700]',
      supervisor: 'bg-[#13983D]/20 text-[#13983D]',
      referencia_tecnica: 'bg-[#F9B047]/20 text-[#F9B047]',
      professor: 'bg-[#7B61FF]/20 text-[#7B61FF]',
      gestor: 'bg-slate-500/20 text-slate-400',
    };
    return colors[role] || 'bg-slate-500/20 text-slate-400';
  };

  // Estatísticas
  const stats = {
    total: usuarios.length,
    ativos: usuarios.filter(u => u.is_active).length,
    inativos: usuarios.filter(u => !u.is_active).length,
    admins: usuarios.filter(u => u.role === 'administrador').length,
    coordenadores: usuarios.filter(u => u.role === 'coordenador').length,
    diretores: usuarios.filter(u => u.role === 'diretor').length,
    gestores: usuarios.filter(u => u.role === 'gestor').length,
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Usuários..."
        //subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`p-6 rounded-xl text-center ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className={darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}>{error}</p>
          <button
            onClick={fetchData}
            className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              }`}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Gerenciar Usuários
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Administre os usuários do sistema
            {userCity && <span className="text-sideb-blue"> - {userCity.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          {geraAutocadastro && (
            <Link
              to="/usuarios/autocadastro"
              className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                ? 'bg-slate-800/70 text-slate-200 border border-slate-700 hover:bg-slate-700'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm'}`}
              title="Links de autocadastro, cadastros aguardando aprovação e quem entrou por cada link"
            >
              Autocadastro
              {pendentesAutocadastro > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E83A3C] text-white text-xs font-bold flex items-center justify-center">
                  {pendentesAutocadastro}
                </span>
              )}
            </Link>
          )}
          {canCreate && (
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Novo Usuário
            </button>
          )}
        </div>
      </header>

      {geraAutocadastro && pendentesAutocadastro > 0 && (
        <Link
          to="/usuarios/autocadastro"
          className="block w-full mb-4 px-4 py-3 rounded-xl text-sm text-left font-medium bg-[#F9B047]/15 text-[#B26B00] border border-[#F9B047]/30 hover:bg-[#F9B047]/25"
        >
          {pendentesAutocadastro === 1
            ? '1 cadastro aguardando aprovação'
            : `${pendentesAutocadastro} cadastros aguardando aprovação`} — clique para revisar
        </Link>
      )}

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
            {stats.total}
          </div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-[#13983D]">{stats.ativos}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ativos</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-[#E83A3C]">{stats.inativos}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Inativos</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-[#2E4974]">{stats.admins}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Admins</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-[#0974BA]">{stats.coordenadores}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Coordenadores</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-[#B07700]">{stats.diretores}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Diretores</div>
        </div>
        <div className={`rounded-xl p-4 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-2xl font-bold text-slate-500">{stats.gestores}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gestores</div>
        </div>
      </div>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Busca */}
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Buscar por nome, email ou usuário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />
          </div>

          {/* Filtro por Role */}
          <SearchableSelect
            value={filtroRole}
            onChange={setFiltroRole}
            options={roles}
            emptyOptionLabel="Todos os perfis"
            darkMode={darkMode}
            className="min-w-[180px]"
          />

          {/* Filtro por Cidade */}
          <SearchableSelect
            value={filtroCidade}
            onChange={setFiltroCidade}
            options={cidades.map(c => ({ value: c.id, label: `${c.city} - ${c.state}` }))}
            emptyOptionLabel="Todas as cidades"
            darkMode={darkMode}
            className="min-w-[200px]"
          />

          {/* Filtro por Status */}
          <SearchableSelect
            value={filtroStatus}
            onChange={setFiltroStatus}
            options={[
              { value: 'ativo', label: 'Ativos' },
              { value: 'inativo', label: 'Inativos' },
            ]}
            emptyOptionLabel="Todos os status"
            darkMode={darkMode}
            className="min-w-[150px]"
          />
        </div>

        {/* Limpar filtros */}
        {(filtroRole || filtroCidade || filtroStatus || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className={`text-sm ${darkMode ? 'text-[#0974BA] hover:text-[#0974BA]' : 'text-[#0974BA] hover:text-[#0974BA]'}`}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabela de Usuários */}
      <div className={`rounded-2xl p-6 ${darkMode
        ? 'bg-slate-800/70 border border-slate-700/10'
        : 'bg-white/90 border border-slate-200 shadow-sm'
        }`}>
        {usuariosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">👤</div>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Nenhum usuário encontrado
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {usuarios.length === 0
                ? 'Não há usuários cadastrados ainda.'
                : 'Nenhum usuário corresponde aos filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Usuário</th>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Email</th>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Perfil</th>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Cidade</th>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                  <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((u) => (
                  <tr key={u.id} className={`border-b ${darkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                    <td className={`py-3 px-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      <div>
                        <div className="font-medium">{u.first_name} {u.last_name}</div>
                        <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>@{u.username}</div>
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{u.email}</td>
                    <td className="py-3 px-4">
                      {/* Uma linha é um acesso, não uma pessoa: quem acumula
                          funções aparece mais de uma vez. Sem mostrar os
                          outros acessos aqui, a lista sugere pessoas
                          diferentes com o mesmo nome. */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                          {getRoleLabel(u.role)}
                        </span>
                        {(u.outros_acessos || []).map((outro) => (
                          <span
                            key={outro.id}
                            title={`Outro acesso da mesma pessoa: @${outro.username}`}
                            className={`px-2 py-1 rounded text-xs border border-dashed ${
                              darkMode
                                ? 'border-slate-600 text-slate-400'
                                : 'border-slate-300 text-slate-500'
                            }`}
                          >
                            {outro.role_display || getRoleLabel(outro.role)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {u.city_name || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => canEdit && handleToggleActive(u)}
                        disabled={!canEdit}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${u.is_active
                          ? 'bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30'
                          : 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
                          } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(u)}
                          className={`p-1.5 rounded-lg transition-colors ${darkMode
                            ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                            : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                            }`}
                          title="Ver detalhes"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {canEdit && (
                          <button
                            onClick={() => handleEdit(u)}
                            className={`p-1.5 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-slate-600 text-slate-400 hover:text-slate-200'
                              : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                              }`}
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}

                        {temEscolaAtribuida(u.role) && canAssignSchools && (
                          <button
                            onClick={() => handleManageSchools(u)}
                            className={`p-1.5 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-[#0974BA]/20 text-[#0974BA] hover:text-[#0974BA]'
                              : 'hover:bg-[#0974BA]/10 text-[#0974BA] hover:text-[#0974BA]'
                              }`}
                            title="Gerenciar escolas"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </button>
                        )}

                        {hasRole(ROLES.ADMIN) && (
                          <button
                            onClick={() => handleResetPasswordClick(u)}
                            className={`p-1.5 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-[#F9B047]/20 text-[#F9B047] hover:text-[#F9B047]'
                              : 'hover:bg-[#F9B047]/10 text-[#F9B047] hover:text-[#F9B047]'
                              }`}
                            title="Redefinir senha"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                          </button>
                        )}

                        {canDelete && u.id !== user?.id && (
                          <button
                            onClick={() => handleDeleteClick(u)}
                            className={`p-1.5 rounded-lg transition-colors ${darkMode
                              ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                              : 'hover:bg-[#E83A3C]/10 text-slate-500 hover:text-[#E83A3C]'
                              }`}
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nome */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => {
                    const first_name = e.target.value;
                    setFormData((f) => ({
                      ...f,
                      first_name,
                      username: usernameEditado ? f.username : sugerirUsername(first_name, f.last_name),
                      usernames: recalcularUsernames(
                        first_name, f.last_name, f.roles, f.usernames, usernamesEditados
                      ),
                    }));
                  }}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.first_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: João"
                />
                {formErrors.first_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.first_name}</p>
                )}
              </div>

              {/* Sobrenome */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Sobrenome *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => {
                    const last_name = e.target.value;
                    setFormData((f) => ({
                      ...f,
                      last_name,
                      username: usernameEditado ? f.username : sugerirUsername(f.first_name, last_name),
                      usernames: recalcularUsernames(
                        f.first_name, last_name, f.roles, f.usernames, usernamesEditados
                      ),
                    }));
                  }}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.last_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: Silva"
                />
                {formErrors.last_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.last_name}</p>
                )}
              </div>

              {/* Nome de usuário — na criação ele vem por perfil, mais abaixo */}
              {false && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Nome de usuário (login) *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => {
                      setUsernameEditado(true);
                      setFormData({ ...formData, username: e.target.value.trim() });
                    }}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.username ? 'border-[#E83A3C]' : ''}`}
                    placeholder="Ex: maria.silva"
                    autoComplete="off"
                  />
                  {formErrors.username ? (
                    <p className="text-[#E83A3C] text-xs mt-1">{formErrors.username}</p>
                  ) : (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Sugerido a partir do nome. Para dar um segundo acesso à mesma
                      pessoa, mude aqui (ex.: <span className="font-mono">maria.silva.secretaria</span>).
                    </p>
                  )}
                </div>
              )}

              {/* CPF — opcional, e pode se repetir entre acessos da mesma pessoa */}
              {(
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    CPF <span className={`font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                      const formatted = digits
                        .replace(/(\d{3})(\d)/, '$1.$2')
                        .replace(/(\d{3})(\d)/, '$1.$2')
                        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                      setFormData({ ...formData, cpf: formatted });
                    }}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.cpf ? 'border-[#E83A3C]' : ''}`}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                  {formErrors.cpf ? (
                    <p className="text-[#E83A3C] text-xs mt-1">{formErrors.cpf}</p>
                  ) : (
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Serve de atalho no login. Pode ficar em branco e pode se repetir
                      quando a pessoa tem mais de um acesso.
                    </p>
                  )}
                </div>
              )}

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.email ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: joao@email.com"
                />
                {formErrors.email && (
                  <p className="text-[#E83A3C] text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Telefone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Ex: (11) 99999-9999"
                />
              </div>

              {/* Perfil — na edição, um acesso tem um perfil só */}
              {editingUser ? (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Perfil *
                  </label>
                  <SearchableSelect
                    value={formData.role}
                    onChange={(v) => setFormData({ ...formData, role: v })}
                    options={roles.filter(
                      (r) => r.value !== 'administrador' || user?.role === ROLES.ADMIN
                    )}
                    placeholder="Selecione o perfil"
                    required
                    darkMode={darkMode}
                    className={`w-full ${formErrors.role ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                  />
                  {formErrors.role && (
                    <p className="text-[#E83A3C] text-xs mt-1">{formErrors.role}</p>
                  )}
                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Um acesso tem um perfil só. Para acumular funções, acrescente
                    outro acesso abaixo.
                  </p>
                </div>
              ) : (
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Perfis de acesso *
                  </label>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Marque mais de um quando a pessoa acumula funções. Cada perfil
                    vira um acesso separado, com suas permissões e seu histórico.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {roles
                      .filter((r) => r.value !== 'administrador' || user?.role === ROLES.ADMIN)
                      .map((r) => {
                        const marcado = formData.roles.includes(r.value);
                        return (
                          <label
                            key={r.value}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                              marcado
                                ? 'border-[#0974BA] bg-[#0974BA]/10'
                                : darkMode
                                  ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => alternarPapel(r.value)}
                              className="w-4 h-4 accent-[#0974BA]"
                            />
                            <span className={`text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              {r.label}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                  {formErrors.role && (
                    <p className="text-[#E83A3C] text-xs mt-1">{formErrors.role}</p>
                  )}

                  {/* O que será criado — cada login editável antes de salvar */}
                  {formData.roles.length > 0 && (
                    <div
                      className={`mt-4 rounded-xl border p-4 ${
                        darkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formData.roles.length === 1
                          ? 'Será criado 1 acesso'
                          : `Serão criados ${formData.roles.length} acessos`}
                        {(formData.first_name || formData.last_name)
                          ? ` para ${formData.first_name} ${formData.last_name}`.trimEnd()
                          : ''}
                      </p>
                      <div className="space-y-2">
                        {formData.roles.map((papel) => {
                          const rotulo = roles.find((r) => r.value === papel)?.label || papel;
                          return (
                            <div key={papel} className="flex items-center gap-3">
                              <span
                                className={`text-xs w-32 flex-shrink-0 ${
                                  darkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}
                              >
                                {rotulo}
                              </span>
                              <input
                                type="text"
                                value={formData.usernames[papel] || ''}
                                onChange={(e) => {
                                  const valor = e.target.value.trim();
                                  setUsernamesEditados((s) => ({ ...s, [papel]: true }));
                                  setFormData((f) => ({
                                    ...f,
                                    usernames: { ...f.usernames, [papel]: valor },
                                  }));
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${
                                  darkMode
                                    ? 'bg-slate-800 border-slate-600 text-slate-200'
                                    : 'bg-white border-slate-200 text-slate-700'
                                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                                placeholder="nome de usuário"
                              />
                            </div>
                          );
                        })}
                      </div>
                      {formErrors.usernames && (
                        <p className="text-[#E83A3C] text-xs mt-2">{formErrors.usernames}</p>
                      )}
                      <p className={`text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Todos compartilham nome, CPF, e-mail e a senha inicial. No
                        login, a pessoa escolhe com qual acesso quer entrar.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Acessos desta pessoa — acrescentar um perfil é acrescentar
                  um acesso, com o CPF ligando um ao outro */}
              {editingUser && (
                <div className="md:col-span-2">
                  <div
                    className={`rounded-xl border p-4 ${
                      darkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Acessos desta pessoa
                    </p>

                    {carregandoAcessos ? (
                      <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Carregando...
                      </p>
                    ) : (
                      <>
                        <div className="mt-3 space-y-1.5">
                          {acessosDaPessoa.map((conta) => (
                            <div key={conta.id} className="flex items-center gap-3">
                              <span className={`text-xs w-32 flex-shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {conta.role_display || conta.role}
                              </span>
                              <span className={`text-sm font-mono ${
                                conta.is_active
                                  ? (darkMode ? 'text-slate-200' : 'text-slate-700')
                                  : (darkMode ? 'text-slate-500 line-through' : 'text-slate-400 line-through')
                              }`}>
                                @{conta.username}
                              </span>
                              {conta.id === editingUser.id ? (
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0974BA]/20 text-[#0974BA]">
                                  editando
                                </span>
                              ) : (
                                <div className="flex items-center gap-2 ml-auto">
                                  {!conta.is_active && (
                                    <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                      inativo
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    disabled={mexendoNoAcesso === conta.id}
                                    onClick={() => alternarAtivoDoAcesso(conta)}
                                    className={`text-xs px-2 py-1 rounded border transition-colors disabled:opacity-40 ${
                                      darkMode
                                        ? 'border-slate-600 text-slate-300 hover:border-slate-400'
                                        : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                    }`}
                                  >
                                    {conta.is_active ? 'Desativar' : 'Reativar'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={mexendoNoAcesso === conta.id}
                                    onClick={() => excluirAcesso(conta)}
                                    className="text-xs px-2 py-1 rounded border border-[#E83A3C]/40 text-[#E83A3C] hover:bg-[#E83A3C]/10 transition-colors disabled:opacity-40"
                                  >
                                    Excluir
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          <p className={`text-xs pt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Deixou de exercer uma função? Desative o acesso dela — o
                            que já foi lançado continua no lugar. Excluir só quando a
                            conta nasceu errada.
                          </p>
                          {formErrors.acessos && (
                            <p className="text-[#E83A3C] text-xs pt-1">{formErrors.acessos}</p>
                          )}
                        </div>

                        {!formData.cpf && acessosDaPessoa.length <= 1 && (
                          <p className={`text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            É o CPF que liga um acesso ao outro. Preencha o CPF acima
                            e salve antes de acrescentar, para que a pessoa possa
                            escolher entre eles no login.
                          </p>
                        )}

                        {perfisDisponiveis.length > 0 && (
                          <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              Acrescentar outro perfil
                            </p>
                            <p className={`text-xs mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              Cada perfil marcado vira um acesso novo, com o mesmo
                              nome, CPF e e-mail. Os perfis que ela já tem não
                              aparecem aqui.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {perfisDisponiveis
                                .filter((r) => r.value !== 'administrador' || user?.role === ROLES.ADMIN)
                                .map((r) => {
                                  const marcado = novosPerfis.includes(r.value);
                                  return (
                                    <label
                                      key={r.value}
                                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                                        marcado
                                          ? 'border-[#0974BA] bg-[#0974BA]/10'
                                          : darkMode
                                            ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                                            : 'bg-white border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={marcado}
                                        onChange={() =>
                                          alternarNovoPerfil(r.value, formData.first_name, formData.last_name)
                                        }
                                        className="w-4 h-4 accent-[#0974BA]"
                                      />
                                      <span className={`text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                        {r.label}
                                      </span>
                                    </label>
                                  );
                                })}
                            </div>

                            {novosPerfis.length > 0 && (
                              <div className="mt-4 space-y-2">
                                {novosPerfis.map((papel) => {
                                  const rotulo =
                                    perfisDisponiveis.find((r) => r.value === papel)?.label || papel;
                                  return (
                                    <div key={papel} className="flex items-center gap-3">
                                      <span className={`text-xs w-32 flex-shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {rotulo}
                                      </span>
                                      <input
                                        type="text"
                                        value={novosLogins[papel] || ''}
                                        onChange={(e) => {
                                          const valor = e.target.value.trim();
                                          setNovosLoginsEditados((s) => ({ ...s, [papel]: true }));
                                          setNovosLogins((l) => ({ ...l, [papel]: valor }));
                                        }}
                                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${
                                          darkMode
                                            ? 'bg-slate-800 border-slate-600 text-slate-200'
                                            : 'bg-white border-slate-200 text-slate-700'
                                        } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                                        placeholder="nome de usuário"
                                      />
                                    </div>
                                  );
                                })}

                                {formErrors.novosAcessos && (
                                  <p className="text-[#E83A3C] text-xs">{formErrors.novosAcessos}</p>
                                )}
                                <p className={`text-xs pt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  Sem senha nova: o acesso entra com a mesma senha que
                                  ela já usa. No login, ela digita o CPF e escolhe com
                                  qual acesso quer entrar.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Vínculo com Professor (apenas quando há acesso de professor) */}
              {(editingUser ? formData.role === 'professor' : formData.roles.includes('professor')) && (
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Professor vinculado *
                  </label>
                  <SearchableSelect
                    value={formData.id_teacher || ''}
                    onChange={(v) => setFormData({ ...formData, id_teacher: v })}
                    options={professores.map(p => ({ value: p.id, label: p.teacher_name }))}
                    placeholder="Selecione um professor..."
                    darkMode={darkMode}
                    className={`w-full ${formErrors.id_teacher ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                  />
                  {formErrors.id_teacher && (
                    <p className="text-[#E83A3C] text-xs mt-1">{formErrors.id_teacher}</p>
                  )}
                  <div className="mt-2">
                    {!showCreateTeacher ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateTeacher(true);
                          setCreateTeacherError('');
                          // Pré-preenche o nome a partir do que já foi digitado
                          const fullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
                          setNewTeacherForm(prev => ({
                            ...prev,
                            teacher_name: prev.teacher_name || fullName,
                          }));
                        }}
                        className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${darkMode
                          ? 'bg-[#0974BA]/20 text-[#0974BA] hover:bg-[#0974BA]/30'
                          : 'bg-[#0974BA]/10 text-[#0974BA] hover:bg-[#0974BA]/20'
                          }`}
                      >
                        + Criar novo cadastro de Professor
                      </button>
                    ) : (
                      <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-700/40 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Novo cadastro de Professor
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={newTeacherForm.teacher_name}
                            onChange={(e) => setNewTeacherForm({ ...newTeacherForm, teacher_name: e.target.value })}
                            placeholder="Nome completo"
                            className={`w-full px-3 py-2 rounded-lg text-sm border ${darkMode
                              ? 'bg-slate-700 border-slate-600 text-slate-200'
                              : 'bg-white border-slate-200 text-slate-700'
                              } focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                          />
                          <input
                            type="number"
                            value={newTeacherForm.teacher_serial}
                            onChange={(e) => setNewTeacherForm({ ...newTeacherForm, teacher_serial: e.target.value })}
                            placeholder="Matrícula"
                            className={`w-full px-3 py-2 rounded-lg text-sm border ${darkMode
                              ? 'bg-slate-700 border-slate-600 text-slate-200'
                              : 'bg-white border-slate-200 text-slate-700'
                              } focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                          />
                        </div>
                        {createTeacherError && (
                          <p className="text-[#E83A3C] text-xs mt-2">{createTeacherError}</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button
                            type="button"
                            onClick={handleCreateTeacher}
                            disabled={creatingTeacher}
                            className="px-3 py-1.5 bg-[#0974BA] text-white rounded-lg text-xs font-medium hover:bg-[#0974BA]/90 transition-colors disabled:opacity-50"
                          >
                            {creatingTeacher ? 'Cadastrando...' : 'Cadastrar e vincular'}
                          </button>
                          <button
                            type="button"
                            onClick={resetCreateTeacherForm}
                            disabled={creatingTeacher}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${darkMode
                              ? 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                              } disabled:opacity-50`}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Selecione um cadastro existente ou crie um novo. As turmas/disciplinas que esse professor leciona são definidas em <span className="font-medium">Técnico Referência do Município</span>.
                  </p>
                </div>
              )}

              {/* Cidade */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Cidade
                </label>
                <SearchableSelect
                  value={formData.city}
                  onChange={(v) => setFormData({ ...formData, city: v })}
                  options={cidades.map(c => ({ value: c.id, label: `${c.city} - ${c.state}` }))}
                  emptyOptionLabel="Sem cidade (acesso global)"
                  darkMode={darkMode}
                  className="w-full"
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Usuários sem cidade têm acesso a dados de todas as cidades (exceto gestores)
                </p>
              </div>

              {/* Senha - apenas na criação */}
              {!editingUser && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Senha *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                        : 'bg-slate-50 border-slate-200 text-slate-700'
                        } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.password ? 'border-[#E83A3C]' : ''}`}
                      placeholder="Minimo 6 caracteres"
                    />
                    {formErrors.password && (
                      <p className="text-[#E83A3C] text-xs mt-1">{formErrors.password}</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Confirmar Senha *
                    </label>
                    <input
                      type="password"
                      value={formData.password_confirm}
                      onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                      className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                        ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                        : 'bg-slate-50 border-slate-200 text-slate-700'
                        } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.password_confirm ? 'border-[#E83A3C]' : ''}`}
                      placeholder="Repita a senha"
                    />
                    {formErrors.password_confirm && (
                      <p className="text-[#E83A3C] text-xs mt-1">{formErrors.password_confirm}</p>
                    )}
                  </div>
                </>
              )}

              {/* Status ativo - apenas na edição */}
              {editingUser && (
                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                    />
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Usuário ativo
                    </span>
                  </label>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Usuários inativos não podem fazer login no sistema
                  </p>
                </div>
              )}
            </div>

            {formErrors.submit && (
              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{formErrors.submit}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => { setShowModal(false); resetCreateTeacherForm(); }}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${saving
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                  }`}
              >
                {saving ? 'Salvando...' : editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão com Senha */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDelete}
        item={userToDelete}
        itemName={`${userToDelete?.first_name} ${userToDelete?.last_name} (@${userToDelete?.username})`}
        itemType="Usuário"
        warningMessage="Todos os dados do usuário serão removidos. Digite sua senha para confirmar."
      />

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {selectedUser.first_name} {selectedUser.last_name}
                </h2>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>@{selectedUser.username}</p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode
                  ? 'hover:bg-slate-700 text-slate-400'
                  : 'hover:bg-slate-100 text-slate-500'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Informações Gerais */}
              <div>
                <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Informações Gerais
                </h3>
                <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Email</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.email}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Telefone</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.phone || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>CPF</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.cpf || '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Perfil</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(selectedUser.role)}`}>
                        {getRoleLabel(selectedUser.role)}
                      </span>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cidade</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.city_name || 'Acesso global'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Status</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${selectedUser.is_active
                        ? 'bg-[#13983D]/20 text-[#13983D]'
                        : 'bg-[#E83A3C]/20 text-[#E83A3C]'
                        }`}>
                        {selectedUser.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Cadastro</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.date_joined ? new Date(selectedUser.date_joined).toLocaleDateString('pt-BR') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Último Login</p>
                      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString('pt-BR') : 'Nunca'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Escolas Atribuídas (Supervisor e Diretor) */}
              {temEscolaAtribuida(selectedUser.role) && (
                <div>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Escolas Atribuídas
                  </h3>
                  <div className={`rounded-xl p-4 ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    {loadingSchools ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-2 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : userSchools.length === 0 ? (
                      <p className={`text-sm text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Nenhuma escola atribuída
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {userSchools.map(escola => (
                          <div
                            key={escola.id}
                            className={`px-3 py-2 rounded-lg ${darkMode ? 'bg-slate-600/50' : 'bg-white'}`}
                          >
                            <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              {escola.school}
                            </p>
                            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {escola.city_name || 'Cidade não definida'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Atribuição de Escolas */}
      {showSchoolsModal && selectedManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Gerenciar Escolas
            </h2>
            <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Atribua escolas a <strong>{selectedManager.first_name} {selectedManager.last_name}</strong>
            </p>

            <input
              type="search"
              placeholder="Buscar escola pelo nome ou cidade..."
              value={buscaEscola}
              onChange={(e) => setBuscaEscola(e.target.value)}
              autoFocus
              className={`w-full mb-4 px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
            />

            {loadingSchools ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-[#0974BA] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {escolas.length === 0 ? (
                  <p className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Nenhuma escola disponível
                  </p>
                ) : escolasDoModal.length === 0 ? (
                  <p className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Nenhuma escola encontrada para "{buscaEscola.trim()}"
                  </p>
                ) : (
                  escolasDoModal.map(escola => (
                    <label
                      key={escola.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedSchools.includes(escola.id)
                        ? darkMode ? 'bg-[#0974BA]/20 border border-[#0974BA]/50' : 'bg-[#0974BA]/5 border border-[#0974BA]/20'
                        : darkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSchools.includes(escola.id)}
                        onChange={() => toggleSchoolSelection(escola.id)}
                        className="w-5 h-5 rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {escola.school}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {escola.city_name || 'Cidade não definida'}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700/10">
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {selectedSchools.length} escola(s) selecionada(s)
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSchoolsModal(false);
                    setSelectedManager(null);
                    setSelectedSchools([]);
                  }}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                    ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  disabled={savingSchools}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveSchools}
                  disabled={savingSchools}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${savingSchools
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                    }`}
                >
                  {savingSchools ? 'Salvando...' : 'Salvar Atribuicoes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {showResetPasswordModal && userToResetPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Redefinir Senha
            </h2>
            <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {userToResetPassword.first_name || userToResetPassword.last_name
                ? `${userToResetPassword.first_name || ''} ${userToResetPassword.last_name || ''}`.trim()
                : userToResetPassword.username}
              {' · '}
              <span className="opacity-80">@{userToResetPassword.username}</span>
            </p>

            {/* Caminho normal: a pessoa escolhe a própria senha pelo link.
                Definir a senha aqui obrigaria a combinar a senha por fora —
                WhatsApp, papel — e deixaria o administrador sabendo a senha
                de outra pessoa. */}
            {!definirSenhaNaMao && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${darkMode
                  ? 'bg-[#0974BA]/10 border border-[#0974BA]/20'
                  : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Enviar o link de redefinição para:
                  </p>
                  <p className={`text-sm font-semibold mt-1 break-all ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {userToResetPassword.email}
                  </p>
                  <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    A pessoa escolhe a própria senha. O link vale por 24 horas
                    e, até ela usar, a senha atual continua funcionando.
                  </p>
                </div>

                {passwordResetOk && (
                  <div className={`p-3 rounded-lg ${darkMode
                    ? 'bg-[#13983D]/10 border border-[#13983D]/20'
                    : 'bg-[#13983D]/5 border border-[#13983D]/20'}`}>
                    <p className="text-sm text-[#13983D]">{passwordResetOk}</p>
                  </div>
                )}

                {passwordResetError && (
                  <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                    <p className="text-sm text-[#E83A3C]">{passwordResetError}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setDefinirSenhaNaMao(true); setPasswordResetError(''); setPasswordResetOk(''); }}
                  className={`text-xs underline ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-600'}`}
                >
                  Prefiro definir uma senha manualmente
                </button>
              </div>
            )}

            {definirSenhaNaMao && (
            <div className="space-y-4">
              {!userToResetPassword.email && (
                <div className={`p-3 rounded-lg ${darkMode
                  ? 'bg-[#F9B047]/10 border border-[#F9B047]/20'
                  : 'bg-[#F9B047]/5 border border-[#F9B047]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Este acesso não tem e-mail cadastrado, então não há link a
                    enviar. Combine a senha com a pessoa — ela terá de trocá-la
                    no próximo login.
                  </p>
                </div>
              )}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nova Senha
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Minimo 6 caracteres"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Confirmar Nova Senha
                </label>
                <input
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  placeholder="Repita a nova senha"
                />
              </div>

              {passwordResetError && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{passwordResetError}</p>
                </div>
              )}

              {userToResetPassword.email && (
                <button
                  type="button"
                  onClick={() => { setDefinirSenhaNaMao(false); setPasswordResetError(''); }}
                  className={`text-xs underline ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-600'}`}
                >
                  Voltar e enviar o link por e-mail
                </button>
              )}
            </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setUserToResetPassword(null);
                  setNewPassword('');
                  setNewPasswordConfirm('');
                  setPasswordResetError('');
                  setPasswordResetOk('');
                  setDefinirSenhaNaMao(false);
                }}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={resettingPassword}
              >
                {passwordResetOk ? 'Fechar' : 'Cancelar'}
              </button>
              {definirSenhaNaMao ? (
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${resettingPassword
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#F9B047] to-[#E83A3C] hover:shadow-lg hover:shadow-[#F9B047]/25'
                    }`}
                >
                  {resettingPassword ? 'Salvando...' : 'Definir senha'}
                </button>
              ) : (
                <button
                  onClick={handleEnviarLinkSenha}
                  disabled={resettingPassword || !!passwordResetOk}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${resettingPassword || passwordResetOk
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                    }`}
                >
                  {resettingPassword ? 'Enviando...' : passwordResetOk ? 'Enviado' : 'Enviar link por e-mail'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Usuarios;
